import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Calendar, DollarSign, CheckCircle, Clock, FileText, RefreshCw } from 'lucide-react';
import { Toast } from '../Toast'; // Check relative path if needed, usually ../Toast or ./Toast depending on folder structure

interface CreatePlanModalProps {
    onClose: () => void;
    onSuccess: () => void;
    planToEdit?: any; // Add optional plan for editing
}

export const CreatePlanModal: React.FC<CreatePlanModalProps> = ({ onClose, onSuccess, planToEdit }) => {
    // Initialize state with planToEdit values or defaults
    const [name, setName] = useState(planToEdit?.name || '');
    const [emoji, setEmoji] = useState(planToEdit?.emoji || '💎');
    const [groupId, setGroupId] = useState<string | ''>(planToEdit?.group_id || '');
    const [durationType, setDurationType] = useState<string>(planToEdit?.duration_type || '');
    const [durationValue, setDurationValue] = useState(planToEdit?.duration_value?.toString() || '');
    const [price, setPrice] = useState(planToEdit?.price?.toString() || '');
    const [description, setDescription] = useState(planToEdit?.description || '');
    const [testGateway, setTestGateway] = useState(planToEdit?.test_gateway || false);

    const [groups, setGroups] = useState<any[]>([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

    const fetchGroups = async () => {
        setLoadingGroups(true);
        try {
            const res = await api.get('/groups');
            setGroups(res.data);
        } catch (e) {
            console.error(e);
            // Dont toast error on mount, just leave empty or user can use sync
        } finally {
            setLoadingGroups(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    useEffect(() => {
        if (planToEdit) {
            setName(planToEdit.name || '');
            setEmoji(planToEdit.emoji || '💎');
            setGroupId(planToEdit.group_id != null ? String(planToEdit.group_id) : '');
            setDurationType(planToEdit.duration_type || '');
            setDurationValue(planToEdit.duration_value?.toString() || '');
            setPrice(planToEdit.price?.toString() || '');
            setDescription(planToEdit.description || '');
            setTestGateway(planToEdit.test_gateway === true);
        }
    }, [planToEdit]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            // In Node we usually need account_id for sync, but if we want to sync all:
            await api.post('/groups/sync', { account_id: 'all' });
            setToast({ msg: "Grupos sincronizados do Telegram!", type: 'success' });
            await fetchGroups();
        } catch (e) {
            console.error(e);
            setToast({ msg: "Erro ao sincronizar. Verifique se o backend está online.", type: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const handleSave = async () => {
        setSubmitted(true);
        if (!name) return setToast({ msg: "Nome do plano é obrigatório.", type: 'error' });
        if (!groupId) return setToast({ msg: "Selecione um grupo do Telegram.", type: 'error' });
        if (!durationType) return setToast({ msg: "Selecione o tipo de duração.", type: 'error' });
        if (durationType !== 'lifetime' && !durationValue) return setToast({ msg: "Informe a duração.", type: 'error' });
        if (!price) return setToast({ msg: "Informe o preço.", type: 'error' });

        setSaving(true);
        try {
            const payload = {
                name,
                emoji: emoji || '💎',
                group_id: groupId && groupId.trim() !== '' ? groupId.trim() : null,
                duration_type: durationType,
                duration_value: durationType === 'lifetime' ? 9999 : parseInt(durationValue),
                price: parseFloat(price.replace(',', '.')),
                description,
                test_gateway: testGateway
            };

            console.log('Payload sendo enviado:', payload);

            if (planToEdit) {
                await api.put(`/plans/${planToEdit.id}`, payload);
                setToast({ msg: "Plano atualizado!", type: 'success' });
            } else {
                await api.post('/plans', payload);
                setToast({ msg: "Plano criado!", type: 'success' });
            }

            // Small delay to show success toast
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 1000);

        } catch (e: any) {
            console.error(e);
            const errorMsg = e.response?.data?.detail || "Erro ao salvar plano.";
            setToast({ msg: errorMsg, type: 'error' });
            setSaving(false); // Only stop loading if error (otherwise wait for timeout)
        }
    };

    return (
        <div className="create-plan-modal" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="create-plan-modal-inner modal-content-premium" style={{
                background: '#09090b',
                borderRadius: '24px',
                width: '1100px',
                maxWidth: '95%',
                border: '1px solid #333',
                color: 'white',
                boxShadow: '0 40px 80px rgba(0, 0, 0, 0.7)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div className="create-plan-modal-header" style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #282828',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#1a1a1c'
                }}>
                    <div>
                        <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0, letterSpacing: '-0.5px', color: '#ff6a00' }}>
                            {planToEdit ? `Editando: ${planToEdit.name}` : 'Criar Plano de Assinatura'}
                        </h2>
                        <div style={{ fontSize: '14px', color: '#888', marginTop: '4px' }}>
                            {planToEdit ? 'Atualize as informações do plano abaixo' : 'Configure um novo plano para monetizar seu bot no Telegram'}
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'transparent', border: 'none', color: '#666',
                        cursor: 'pointer', padding: '4px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: '0.2s'
                    }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body with Preview */}
                <div className="create-plan-modal-body" style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                    {/* Left Side - Form */}
                    <div className="create-plan-form" style={{ flex: 1, padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto', maxHeight: '70vh' }}>
                        {/* Row 1: Emoji, Name and Group */}
                        <div className="create-plan-row create-plan-row-1" style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '20px' }}>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                                    Emoji
                                </label>
                                <input
                                    type="text"
                                    style={{
                                        width: '100%', background: '#121214', border: '1px solid #333',
                                        borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '20px', outline: 'none',
                                        textAlign: 'center'
                                    }}
                                    placeholder="💎"
                                    maxLength={2}
                                    value={emoji}
                                    onChange={e => setEmoji(e.target.value)}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                                    Nome do Plano <span style={{ color: '#ab3232' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    style={{
                                        width: '100%', background: '#121214', border: name ? '1px solid #333' : '1px solid #ab3232',
                                        borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none'
                                    }}
                                    placeholder="Ex: Plano Premium"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                />
                                {submitted && !name && <div style={{ color: '#ab3232', fontSize: '11px', marginTop: '4px' }}>Nome do plano é obrigatório</div>}
                            </div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#fff', margin: 0 }}>
                                        Grupo Telegram <span style={{ color: '#ab3232' }}>*</span>
                                    </label>
                                    <button
                                        onClick={handleSync}
                                        disabled={syncing}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#ff6a00',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            transition: '0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 106, 0, 0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <RefreshCw size={12} className={syncing ? 'spin-animation' : ''} />
                                        {syncing ? 'Sincronizando...' : 'Sincronizar'}
                                    </button>
                                </div>
                                <select
                                    style={{
                                        width: '100%', background: '#121214', border: '1px solid #333',
                                        borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none',
                                        cursor: 'pointer'
                                    }}
                                    value={groupId}
                                    onChange={e => setGroupId(e.target.value)}
                                >
                                    <option value="">Selecione um grupo</option>
                                    {loadingGroups ? (
                                        <option disabled>Carregando grupos...</option>
                                    ) : groups.length === 0 ? (
                                        <option disabled>Nenhum grupo encontrado (Clique Sincronizar)</option>
                                    ) : (
                                        groups
                                            .sort((a, b) => a.title.localeCompare(b.title))
                                            .map(g => (
                                                <option key={g.id} value={g.id}>
                                                    {g.title} {g.username ? `(@${g.username})` : ''}
                                                </option>
                                            ))
                                    )}
                                </select>
                            </div>
                        </div>

                        {/* Row 2: Duration Type and Value */}
                        <div className="create-plan-row create-plan-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                                    Tipo de Duração <span style={{ color: '#ab3232' }}>*</span>
                                </label>
                                <select
                                    style={{
                                        width: '100%', background: '#121214', border: '1px solid #333',
                                        borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none'
                                    }}
                                    value={durationType}
                                    onChange={e => setDurationType(e.target.value)}
                                >
                                    <option value="">Selecione o tipo</option>
                                    <option value="days">Dias</option>
                                    <option value="months">Meses</option>
                                    <option value="lifetime">Vitalício</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                                    Duração <span style={{ color: '#ab3232' }}>*</span>
                                </label>
                                <input
                                    type="number"
                                    disabled={durationType === 'lifetime'}
                                    style={{
                                        width: '100%', background: durationType === 'lifetime' ? '#1a1a1c' : '#121214',
                                        border: '1px solid #333', opacity: durationType === 'lifetime' ? 0.5 : 1,
                                        borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none'
                                    }}
                                    placeholder={durationType === 'lifetime' ? '∞' : "Ex: 7, 30, 365"}
                                    value={durationValue}
                                    onChange={e => setDurationValue(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Price */}
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#ff6a00', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Preço (R$) <span style={{ color: '#ab3232' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    style={{
                                        width: '100%', background: '#121214', border: '1px solid #333',
                                        borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none'
                                    }}
                                    placeholder="0,00"
                                    value={price}
                                    onChange={e => {
                                        // Basic mask for currency
                                        const val = e.target.value.replace(/[^0-9,.]/g, '');
                                        setPrice(val);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#ff6a00', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Descrição
                            </label>
                            <textarea
                                style={{
                                    width: '100%', background: '#121214', border: '1px solid #333',
                                    borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none',
                                    minHeight: '100px', resize: 'vertical', fontFamily: 'inherit'
                                }}
                                placeholder="Descreva os benefícios deste plano..."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                Descrição detalhada do plano (opcional) ({description.length}/500)
                            </div>
                        </div>

                        {/* Test Gateway Toggle */}
                        <div style={{
                            background: testGateway ? 'rgba(251, 191, 36, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                            border: testGateway ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid #333',
                            borderRadius: '12px',
                            padding: '16px 20px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>⚡ Gateway de Teste</span>
                                        {testGateway && <span style={{ fontSize: '10px', background: '#fbbf24', color: '#78350f', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>ATIVO</span>}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#999' }}>
                                        {testGateway ? 'Pagamentos serão simulados automaticamente (sem cobrança real)' : 'Ative para testar o fluxo de pagamento sem cobranças reais'}
                                    </div>
                                </div>
                                <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={testGateway}
                                        onChange={(e) => setTestGateway(e.target.checked)}
                                        style={{ opacity: 0, width: 0, height: 0 }}
                                    />
                                    <span style={{
                                        position: 'absolute',
                                        cursor: 'pointer',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: testGateway ? '#fbbf24' : '#333',
                                        transition: '0.3s',
                                        borderRadius: '26px'
                                    }}>
                                        <span style={{
                                            position: 'absolute',
                                            content: '',
                                            height: '18px',
                                            width: '18px',
                                            left: testGateway ? '28px' : '4px',
                                            bottom: '4px',
                                            background: 'white',
                                            transition: '0.3s',
                                            borderRadius: '50%'
                                        }}></span>
                                    </span>
                                </label>
                            </div>
                        </div>

                    </div>

                    {/* Right Side - Live Preview */}
                    <div className="create-plan-preview" style={{
                        width: '380px',
                        background: '#0a0a0c',
                        borderLeft: '1px solid #282828',
                        padding: '32px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <div>
                            <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#ff6a00', margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '18px' }}>📱</span> Preview do Bot
                            </h3>
                            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                                Veja como o botão aparecerá no Telegram
                            </p>
                        </div>

                        {/* Telegram-like Preview */}
                        <div style={{
                            background: '#1a1a1c',
                            borderRadius: '12px',
                            padding: '16px',
                            border: '1px solid #2a2a2c'
                        }}>
                            <div style={{
                                background: '#2b1d36',
                                borderRadius: '7px',
                                padding: '12px 16px',
                                textAlign: 'center',
                                fontSize: '14.5px',
                                fontWeight: 600,
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: '0.2s',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}>
                                <span style={{ fontSize: '16px' }}>{emoji || '💎'}</span>
                                <span>{name || 'Nome do Plano'} - R$ {price ? parseFloat(price.replace(',', '.')).toFixed(2).replace('.', ',') : '0,00'}</span>
                            </div>
                        </div>

                        <div style={{
                            background: 'rgba(0, 229, 255, 0.05)',
                            border: '1px solid rgba(0, 229, 255, 0.2)',
                            borderRadius: '8px',
                            padding: '12px',
                            fontSize: '11px',
                            color: '#ff6a00',
                            lineHeight: '1.5'
                        }}>
                            💡 <strong>Dica:</strong> Escolha um emoji que represente bem o seu plano. Ele aparecerá em todas as mensagens do bot!
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="create-plan-modal-footer" style={{
                    padding: '20px 24px',
                    borderTop: '1px solid #333',
                    background: '#111',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                }}>
                    <button onClick={onClose} style={{
                        background: 'transparent', border: '1px solid #444', color: '#aaa',
                        padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 500,
                        fontSize: '14px'
                    }}>
                        Cancelar
                    </button>
                    <button onClick={handleSave} disabled={saving} style={{
                        background: 'var(--btn-gradient)',
                        border: 'none', color: '#000',
                        padding: '10px 32px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
                        fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px',
                        boxShadow: 'none'
                    }}>
                        {saving ? 'Salvando...' : (planToEdit ? 'Atualizar Plano' : 'Criar Plano')}
                    </button>
                </div>
            </div>
        </div>
    );
};
